import { CircularProgress, InputAdornment } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { InputRulesHelperText } from '@/components/Form/InputRulesHelperText';
import { TextInput } from '@/components/Form/TextInput';

import { USERNAME_PATTERN } from '@/constants/patterns';
import { USERNAME_RULES } from '@/constants/user';

import { useTranslate } from '@/hooks/useTranslate';

import { useValidateUsername } from './hooks/useValidateUsername';
import { UsernameSuggestions } from './UsernameSuggestions';

type UsernameInputProps = {
  apiError: string;
  disabled: boolean;
  phoneNumber: string;
  birthday?: string;
  placeholder?: string;
  hideLabel?: boolean;
  requiredMessage?: string;
};

export function UsernameInput({
  apiError,
  disabled,
  phoneNumber,
  birthday,
  placeholder,
  hideLabel,
  requiredMessage,
}: Readonly<UsernameInputProps>) {
  const { t } = useTranslate();
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const {
    validateUsername,
    resetValidation,
    suggestedUsernames,
    isValidating,
  } = useValidateUsername(birthday ?? '', phoneNumber);

  const onClickSuggestion = (username: string) => {
    setValue('username', username);
    resetValidation();
    trigger('username');
  };

  useEffect(() => {
    if (!errors.username && suggestedUsernames.length) {
      resetValidation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  return (
    <>
      <TextInput
        name="username"
        control={control}
        error={!!errors.username || !!apiError}
        helperText={
          t(errors.username?.message?.toString() as string) || apiError
        }
        rules={{
          required:
            requiredMessage ??
            t('createNewAccount.basicInfo.form.username.error.required'),
          pattern: {
            value: new RegExp(USERNAME_PATTERN),
            message: t(
              'createNewAccount.basicInfo.form.username.error.pattern',
            ),
          },
          validate: value => validateUsername(value),
        }}
        disabled={disabled}
        fullWidth
        label={
          hideLabel
            ? undefined
            : t('createNewAccount.basicInfo.form.username.label')
        }
        placeholder={placeholder}
        InputProps={{
          endAdornment: isValidating && (
            <InputAdornment position="end">
              <CircularProgress size={16} />
            </InputAdornment>
          ),
        }}
      />
      {suggestedUsernames.length > 0 && (
        <UsernameSuggestions
          usernames={suggestedUsernames}
          onClick={onClickSuggestion}
        />
      )}
      <InputRulesHelperText
        mt={-2}
        currentValue={watch('username')}
        rules={USERNAME_RULES}
      />
    </>
  );
}
